import { PlanFeaturesProvider, usePlanFeatures } from '../../../contexts/PlanFeaturesContext';
import ToolHelpButton from '../../../components/ToolHelpButton';
import OzonAnalytics from './analytics/OzonAnalytics';
import OzonStatistics from './analytics/OzonStatistics';
import OzonSeoUniqualization from './seo/OzonSeoUniqualization';
import OzonSeoAiPanel from './seo/OzonSeoAiPanel';
import AutoSeoPanel from './seo/AutoSeoPanel';
import OzonAssortmentPanel from './assortment/OzonAssortmentPanel';
import OzonAttributesPanel from './assortment/OzonAttributesPanel';
import OzonPaidStorage from './logistics/OzonPaidStorage';
import OzonDescriptionPanel from './seo/OzonDescriptionPanel';
import OzonPrecisionEdits from './seo/OzonPrecisionEdits';
import OzonAdsCampaigns from './advertising/OzonAdsCampaigns';
import OzonBidder from './advertising/OzonBidder';
import OzonRepeatOrders from './advertising/OzonRepeatOrders';
import OzonRepeatOrdersProject from './advertising/OzonRepeatOrdersProject';
import OzonPricesPanel from './prices/OzonPricesPanel';
import OzonFbsPanel from './logistics/OzonFbsPanel';
import OzonFbsChzPrint from './logistics/OzonFbsChzPrint';
import OzonFboPanel from './logistics/OzonFboPanel';
import FeedbackPanel from './feedback/FeedbackPanel';
import OzonUnitEconomics from './economics/OzonUnitEconomics';
import { TaxReportsPanel } from '../../tax-reports';
import DailyStatsLogs from '../shared/DailyStatsLogs';
import WrapOrDropdown from '../shared/WrapOrDropdown';

function OzonSection({
    ozonBusy,
    ozonAccounts,
    ozonProjects,
    ozonProjectId,
    ozonProjectCabinets,
    ozonAccountId,
    ozonTab,
    setOzonTab,
    customerIdFilter,
    selectOzonProject,
    selectOzonAccount,
    openLightbox,
    syncOzonFast,
    syncOzonData,
    assortmentPageSafe,
    assortmentTotalPages,
    assortmentPageSize,
    setAssortmentPage,
    setAssortmentPageSize,
    assortmentSearch,
    setAssortmentSearch,
    copyTable,
    downloadCsv,
    ozonAssortment,
    assortmentSliceStart,
    assortmentHeadersDisplay,
    getStickyClass,
    assortmentSort,
    setAssortmentSort,
    assortmentRowsView,
    attributesPageSafe,
    attributesTotalPages,
    attributesPageSize,
    setAttributesPage,
    setAttributesPageSize,
    attributesSearch,
    setAttributesSearch,
    ozonAttributes,
    attributesHeadersFull,
    attributesRowsView,
    attributesSliceStart,
    attributesSliceEnd,
    attributesSort,
    setAttributesSort,
    ATTRIBUTES_MAX_COLUMNS,
    refreshOzonList,
    moduleFlags = {},
}) {
    const selectedProject = ozonProjects.find((project) => project.id === ozonProjectId);
    const projectName = selectedProject ? selectedProject.name : '';
    const activeAccount = ozonAccounts.find((account) => account.id === ozonAccountId) || null;
    const activeAccountName = activeAccount?.account_name || '';
    const tierMap = {
        basic: 'Basic',
        premium: 'Premium',
        premium_plus: 'Premium Plus',
        premium_pro: 'Premium Pro',
    };
    const activeTierLabel = tierMap[activeAccount?.ozon_subscription_tier] || 'Basic';
    const isVisible = (key) => !['disabled'].includes(moduleFlags?.[key] || 'enabled');
    const isBeta = (key) => (moduleFlags?.[key]) === 'beta';
    const { hasFeature } = usePlanFeatures();
    const can = (featureKey) => hasFeature(featureKey);

    const BetaBadge = () => <span style={{ fontSize: 12, fontWeight: 700, background: '#ffe3a0', color: '#4a2f00', borderRadius: 3, padding: '1px 4px', marginLeft: 5, verticalAlign: 'middle', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>BETA</span>;

    const TAB_TO_DOMAIN = {
        assortment: 'assortment', attributes: 'assortment',
        descriptions: 'seo', 'precision-edits': 'seo', seo: 'seo', 'seo-tags': 'seo', 'seo-ai': 'seo',
        statistics: 'analytics', 'stats-logs': 'analytics', analytics: 'analytics',
        prices: 'prices',
        repricer: 'repricer',
        fbs: 'logistics', fbo: 'logistics', storage: 'logistics', chz_print: 'logistics',
        ads: 'advertising', bidder: 'advertising',
        'repeat-orders': 'analytics',
        'project-repeat-orders': 'analytics',
        'unit-economics': 'finance', 'tax-reports': 'finance',
        feedback: 'feedback',
    };

    const projectTabs = [
        ...(can('ozon_statistics') ? ['statistics'] : []),
        ...(can('ozon_statistics_logs') ? ['stats-logs'] : []),
        ...(isVisible('ozon_analytics') && can('ozon_analytics') ? ['analytics'] : []),
        ...(isVisible('projects') && can('project_repeat_orders') ? ['project-repeat-orders'] : []),
        ...(isVisible('ozon_finance') && can('ozon_unit_economics') ? ['unit-economics'] : []),
    ];
    const accountTabs = [
        ...(isVisible('ozon_assortment') && can('ozon_assortment') ? ['assortment'] : []),
        ...(isVisible('ozon_assortment') && can('ozon_attributes') ? ['attributes'] : []),
        ...(isVisible('ozon_seo') && can('ozon_seo_descriptions') ? ['descriptions'] : []),
        ...(isVisible('ozon_seo') && can('ozon_seo_precision') ? ['precision-edits'] : []),
        ...(isVisible('ozon_seo') && can('ozon_seo_uniqualize') ? ['seo'] : []),
        ...(isVisible('ozon_seo') && can('ozon_seo_tags') ? ['seo-tags'] : []),
        ...(isVisible('ozon_seo') && isVisible('ozon_seo_ai') && can('ozon_seo_ai') ? ['seo-ai'] : []),
        ...(can('ozon_statistics') ? ['statistics'] : []),
        ...(can('ozon_statistics_logs') ? ['stats-logs'] : []),
        ...(isVisible('ozon_analytics') && can('ozon_analytics') ? ['analytics'] : []),
        ...(isVisible('ozon_analytics') && can('ozon_repeat_orders') ? ['repeat-orders'] : []),
        ...(isVisible('ozon_prices') && can('ozon_prices') ? ['prices'] : []),
        ...(isVisible('ozon_repricer') && can('ozon_prices') && (can('ozon_repricer') || can('ozon_map_repricer') || can('ozon_repricer_profit') || can('ozon_price_monitoring')) ? ['repricer'] : []),
        ...(isVisible('ozon_logistics') && can('ozon_fbs') ? ['fbs'] : []),
        ...(isVisible('ozon_logistics') && can('ozon_fbo') ? ['fbo'] : []),
        ...(isVisible('ozon_logistics') && isVisible('ozon_storage') && can('ozon_paid_storage') ? ['storage'] : []),
        ...(isVisible('ozon_logistics') && can('ozon_chz_print') ? ['chz_print'] : []),
        ...(isVisible('ozon_ads') && can('ozon_ads_campaigns') ? ['ads'] : []),
        ...(isVisible('ozon_ads') && can('ozon_bidder') ? ['bidder'] : []),
        ...(isVisible('ozon_finance') && can('ozon_unit_economics') ? ['unit-economics'] : []),
        ...(isVisible('ozon_finance') && isVisible('tax_reports') && can('ozon_tax_reports') ? ['tax-reports'] : []),

        ...((can('feedback_inbox') || can('feedback_questions') || can('feedback_chats') || can('support_auto')) ? ['feedback'] : []),
    ];

    const visibleTabs = ozonAccountId ? accountTabs : projectTabs;
    const effectiveOzonTab = visibleTabs.includes(ozonTab) ? ozonTab : visibleTabs[0];
    if (ozonTab !== effectiveOzonTab) {
        // eslint-disable-next-line no-console
        console.debug('[ozon-url] tab fell back', { ozonTab, effectiveOzonTab, ozonAccountId, visibleTabs });
    }
    const activeDomain = TAB_TO_DOMAIN[effectiveOzonTab] || 'assortment';

    const hasProjects = ozonAccounts.length > 0 && ozonProjects.length > 0;
    const projectItems = hasProjects
        ? ozonProjects.map((project) => ({
              key: project.id,
              label: project.name,
              active: ozonProjectId === project.id,
              onSelect: () => selectOzonProject(project.id),
          }))
        : [];

    return (
        <>
            <div className="panel__head">
                <div>
                    <h3>Инструменты OZON</h3>
                    <p className="muted">Выберите проект и магазин.</p>
                    {ozonBusy && (
                        <div className="loading">
                            <span className="spinner" aria-hidden="true" />
                            <span>Идет загрузка данных, подождите. Это может занять несколько минут.</span>
                        </div>
                    )}
                </div>
                {hasProjects && (
                    <div className="panel__head-tools">
                        <WrapOrDropdown
                            items={projectItems}
                            rowClassName="account-tabs account-tabs--projects"
                            ariaLabel="Проект"
                        />
                    </div>
                )}
            </div>

            {ozonAccounts.length === 0 ? (
                <div className="empty">
                    Подключите кабинет OZON в разделе «Настройки», чтобы увидеть данные.
                </div>
            ) : (
                <>
                    {ozonProjectId && ozonProjectCabinets.length > 0 && (
                        <WrapOrDropdown
                            items={[
                                // Кнопка проекта нужна, только если кабинетов несколько: у одного кабинета проект совпадает с ним.
                                ...(ozonProjectCabinets.length > 1 ? [{
                                    key: '__project__',
                                    label: `Проект${projectName ? ` «${projectName}»` : ''}`,
                                    active: !ozonAccountId,
                                    onSelect: () => selectOzonProject(ozonProjectId),
                                }] : []),
                                ...ozonProjectCabinets.map((account) => ({
                                    key: account.id,
                                    label: account.account_name,
                                    active: ozonAccountId === account.id,
                                    onSelect: () => selectOzonAccount(account.id),
                                })),
                            ]}
                            rowClassName="account-tabs"
                            ariaLabel="Проект и кабинеты"
                        />
                    )}

                    {ozonProjectId && !ozonAccountId && ozonProjectCabinets.length > 1 && (
                        <div className="muted" style={{ marginTop: 8, marginBottom: 4, fontSize: 13 }}>
                            Это консолидированная статистика всех кабинетов, входящих в проект. Если хотите работать с кабинетом, нажмите на кнопку с его названием.
                        </div>
                    )}

                    {ozonProjectId && ozonProjectCabinets.length === 0 && (
                        <div className="empty">
                            В этом проекте нет кабинетов OZON. Добавьте кабинет в проект: «Настройки → Кабинеты, проекты, сотрудники».
                        </div>
                    )}

                    {ozonAccountId && activeAccount && (
                        <div className="muted" style={{ marginTop: 8, marginBottom: 4, fontSize: 13 }}>
                            Тариф OZON: {activeTierLabel}
                        </div>
                    )}

                    {!ozonProjectId && ozonProjects.length > 0 && (
                        <div className="empty" style={{ marginTop: '20px' }}>
                            Выберите проект, чтобы загрузить его кабинеты.
                        </div>
                    )}

                    {ozonProjects.length === 0 && (
                        <WrapOrDropdown
                            items={ozonAccounts.map((account) => ({
                                key: account.id,
                                label: account.account_name,
                                active: ozonAccountId === account.id,
                                onSelect: () => selectOzonAccount(account.id),
                            }))}
                            rowClassName="account-tabs"
                            ariaLabel="Кабинет"
                        />
                    )}

                    {ozonProjectId && !ozonAccountId && (
                        <>
                            <div className="account-tabs account-tabs--secondary">
                                {visibleTabs.includes('statistics') && (
                                    <button
                                        type="button"
                                        className={effectiveOzonTab === 'statistics' ? 'active' : ''}
                                        onClick={() => setOzonTab('statistics')}
                                        title="Запрос по API к маркетплейсу сейчас"
                                    >
                                        Статистика
                                        <ToolHelpButton toolKey="project_statistics" />
                                    </button>
                                )}
                                {visibleTabs.includes('stats-logs') && (
                                    <button
                                        type="button"
                                        className={effectiveOzonTab === 'stats-logs' ? 'active' : ''}
                                        onClick={() => setOzonTab('stats-logs')}
                                        title="Сохранённая статистика за период работы в сервисе"
                                    >
                                        Статистика (Логи)
                                        <ToolHelpButton toolKey="project_statistics_logs" />
                                    </button>
                                )}
                                {visibleTabs.includes('analytics') && (
                                    <button
                                        type="button"
                                        className={effectiveOzonTab === 'analytics' ? 'active' : ''}
                                        onClick={() => setOzonTab('analytics')}
                                    >
                                        Аналитика
                                        <ToolHelpButton toolKey="project_analytics" />
                                    </button>
                                )}
                                {visibleTabs.includes('unit-economics') && (
                                    <button
                                        type="button"
                                        className={effectiveOzonTab === 'unit-economics' ? 'active' : ''}
                                        onClick={() => setOzonTab('unit-economics')}
                                    >
                                        Экономика
                                    </button>
                                )}
                                {visibleTabs.includes('project-repeat-orders') && (
                                    <button
                                        type="button"
                                        className={effectiveOzonTab === 'project-repeat-orders' ? 'active' : ''}
                                        onClick={() => setOzonTab('project-repeat-orders')}
                                    >
                                        Повторные заказы
                                        <ToolHelpButton toolKey="project_repeat_orders" />
                                    </button>
                                )}
                            </div>

                            {effectiveOzonTab === 'statistics' && (
                                <OzonStatistics
                                    accountId={ozonProjectCabinets[0]?.id}
                                    accountName={projectName}
                                    openLightbox={openLightbox}
                                    projectMode={true}
                                    projectCabinets={ozonProjectCabinets}
                                />
                            )}

                            {effectiveOzonTab === 'stats-logs' && (
                                <DailyStatsLogs
                                    accountId={ozonProjectCabinets[0]?.id}
                                    accountName={projectName}
                                    marketplace="ozon"
                                    projectMode={true}
                                    projectCabinets={ozonProjectCabinets}
                                />
                            )}

                            {effectiveOzonTab === 'analytics' && isVisible('ozon_analytics') && (
                                <OzonAnalytics
                                    accountId={ozonProjectCabinets[0]?.id}
                                    accountName={projectName}
                                    openLightbox={openLightbox}
                                    projectMode={true}
                                    projectCabinets={ozonProjectCabinets}
                                />
                            )}

                            {effectiveOzonTab === 'unit-economics' && (
                                <OzonUnitEconomics ozonAccountId={null} projectCabinets={ozonProjectCabinets} />
                            )}

                            {effectiveOzonTab === 'project-repeat-orders' && (
                                <OzonRepeatOrdersProject projectCabinets={ozonProjectCabinets} />
                            )}
                        </>
                    )}

                    {ozonAccountId && (
                        <>
                            {/* Primary domain navigation */}
                            <div className="account-tabs">
                                {(visibleTabs.includes('assortment') || visibleTabs.includes('attributes')) && (
                                    <button type="button" className={activeDomain === 'assortment' ? 'active' : ''} onClick={() => setOzonTab(visibleTabs.includes('assortment') ? 'assortment' : 'attributes')}>
                                        Ассортимент{isBeta('ozon_assortment') && <BetaBadge />}
                                    </button>
                                )}
                                {(visibleTabs.includes('descriptions') || visibleTabs.includes('seo') || visibleTabs.includes('seo-tags') || visibleTabs.includes('seo-ai')) && (
                                    <button type="button" className={activeDomain === 'seo' ? 'active' : ''} onClick={() => setOzonTab(['descriptions','seo','seo-tags','seo-ai'].find(t => visibleTabs.includes(t)))}>
                                        SEO{isBeta('ozon_seo') && <BetaBadge />}
                                    </button>
                                )}
                                {(visibleTabs.includes('statistics') || visibleTabs.includes('stats-logs') || visibleTabs.includes('analytics') || visibleTabs.includes('repeat-orders')) && (
                                    <button type="button" className={activeDomain === 'analytics' ? 'active' : ''} onClick={() => setOzonTab(['statistics','stats-logs','analytics','repeat-orders'].find(t => visibleTabs.includes(t)))}>
                                        Аналитика{isBeta('ozon_analytics') && <BetaBadge />}
                                    </button>
                                )}
                                {visibleTabs.includes('prices') && (
                                    <button type="button" className={activeDomain === 'prices' ? 'active' : ''} onClick={() => setOzonTab('prices')}>
                                        Цены{isBeta('ozon_prices') && <BetaBadge />}
                                    </button>
                                )}
                                {visibleTabs.includes('repricer') && (
                                    <button type="button" className={activeDomain === 'repricer' ? 'active' : ''} onClick={() => setOzonTab('repricer')}>
                                        Репрайсер{isBeta('ozon_repricer') && <BetaBadge />}
                                    </button>
                                )}
                                {(visibleTabs.includes('fbs') || visibleTabs.includes('fbo') || visibleTabs.includes('storage') || visibleTabs.includes('chz_print')) && (
                                    <button type="button" className={activeDomain === 'logistics' ? 'active' : ''} onClick={() => setOzonTab(['fbs','fbo','storage','chz_print'].find(t => visibleTabs.includes(t)))}>
                                        Логистика{isBeta('ozon_logistics') && <BetaBadge />}
                                    </button>
                                )}
                                {visibleTabs.includes('feedback') && (
                                    <button type="button" className={activeDomain === 'feedback' ? 'active' : ''} onClick={() => setOzonTab('feedback')}>
                                        Отзывы, вопросы, чаты
                                    </button>
                                )}
                                {(visibleTabs.includes('ads') || visibleTabs.includes('bidder')) && (
                                    <button type="button" className={activeDomain === 'advertising' ? 'active' : ''} onClick={() => setOzonTab(visibleTabs.includes('ads') ? 'ads' : 'bidder')}>
                                        Реклама{isBeta('ozon_ads') && <BetaBadge />}
                                    </button>
                                )}
                                {(visibleTabs.includes('unit-economics') || visibleTabs.includes('tax-reports')) && (
                                    <button type="button" className={activeDomain === 'finance' ? 'active' : ''} onClick={() => setOzonTab(['unit-economics','tax-reports'].find(t => visibleTabs.includes(t)))}>
                                        Экономика{isBeta('ozon_finance') && <BetaBadge />}
                                    </button>
                                )}
                            </div>

                            {/* Secondary sub-navigation */}
                            {activeDomain === 'assortment' && (
                                <div className="account-tabs account-tabs--secondary">
                                    {visibleTabs.includes('assortment') && <button type="button" className={effectiveOzonTab === 'assortment' ? 'active' : ''} onClick={() => setOzonTab('assortment')}>Ассортимент<ToolHelpButton toolKey="ozon_assortment" /></button>}
                                    {visibleTabs.includes('attributes') && <button type="button" className={effectiveOzonTab === 'attributes' ? 'active' : ''} onClick={() => setOzonTab('attributes')}>Атрибуты<ToolHelpButton toolKey="ozon_attributes" /></button>}
                                </div>
                            )}
                            {activeDomain === 'seo' && (
                                <div className="account-tabs account-tabs--secondary">
                                    {visibleTabs.includes('descriptions') && <button type="button" className={effectiveOzonTab === 'descriptions' ? 'active' : ''} onClick={() => setOzonTab('descriptions')}>Описания<ToolHelpButton toolKey="ozon_seo_descriptions" /></button>}
                                    {visibleTabs.includes('precision-edits') && <button type="button" className={effectiveOzonTab === 'precision-edits' ? 'active' : ''} onClick={() => setOzonTab('precision-edits')}>Точечные изменения<ToolHelpButton toolKey="ozon_seo_precision" /></button>}
                                    {visibleTabs.includes('seo') && <button type="button" className={effectiveOzonTab === 'seo' ? 'active' : ''} onClick={() => setOzonTab('seo')}>Уникализация<ToolHelpButton toolKey="ozon_seo_uniqualize" /></button>}
                                    {visibleTabs.includes('seo-tags') && <button type="button" className={effectiveOzonTab === 'seo-tags' ? 'active' : ''} onClick={() => setOzonTab('seo-tags')}>АвтоSEO<ToolHelpButton toolKey="ozon_seo_tags" /></button>}
                                    {visibleTabs.includes('seo-ai') && <button type="button" className={effectiveOzonTab === 'seo-ai' ? 'active' : ''} onClick={() => setOzonTab('seo-ai')}>SEO AI<ToolHelpButton toolKey="ozon_seo_ai" /></button>}
                                </div>
                            )}
                            {activeDomain === 'analytics' && (
                                <div className="account-tabs account-tabs--secondary">
                                    {visibleTabs.includes('statistics') && <button type="button" className={effectiveOzonTab === 'statistics' ? 'active' : ''} onClick={() => setOzonTab('statistics')} title="Запрос по API к маркетплейсу сейчас">Статистика<ToolHelpButton toolKey="ozon_statistics" /></button>}
                                    {visibleTabs.includes('stats-logs') && <button type="button" className={effectiveOzonTab === 'stats-logs' ? 'active' : ''} onClick={() => setOzonTab('stats-logs')} title="Сохранённая статистика за период работы в сервисе">Логи<ToolHelpButton toolKey="ozon_statistics_logs" /></button>}
                                    {visibleTabs.includes('analytics') && <button type="button" className={effectiveOzonTab === 'analytics' ? 'active' : ''} onClick={() => setOzonTab('analytics')}>Сравнение периодов<ToolHelpButton toolKey="ozon_analytics" /></button>}
                                    {visibleTabs.includes('repeat-orders') && <button type="button" className={effectiveOzonTab === 'repeat-orders' ? 'active' : ''} onClick={() => setOzonTab('repeat-orders')}>Повторные заказы<ToolHelpButton toolKey="ozon_repeat_orders" /></button>}
                                </div>
                            )}
                            {activeDomain === 'logistics' && (
                                <div className="account-tabs account-tabs--secondary">
                                    {visibleTabs.includes('fbs') && <button type="button" className={effectiveOzonTab === 'fbs' ? 'active' : ''} onClick={() => setOzonTab('fbs')}>FBS<ToolHelpButton toolKey="ozon_fbs" /></button>}
                                    {visibleTabs.includes('fbo') && <button type="button" className={effectiveOzonTab === 'fbo' ? 'active' : ''} onClick={() => setOzonTab('fbo')}>FBO<ToolHelpButton toolKey="ozon_fbo" /></button>}
                                    {visibleTabs.includes('storage') && <button type="button" className={effectiveOzonTab === 'storage' ? 'active' : ''} onClick={() => setOzonTab('storage')}>Платное хранение</button>}
                                    {visibleTabs.includes('chz_print') && <button type="button" className={effectiveOzonTab === 'chz_print' ? 'active' : ''} onClick={() => setOzonTab('chz_print')}>Печать ЧЗ<ToolHelpButton toolKey="ozon_chz_print" /></button>}
                                </div>
                            )}
                            {activeDomain === 'advertising' && (
                                <div className="account-tabs account-tabs--secondary">
                                    {visibleTabs.includes('ads') && <button type="button" className={effectiveOzonTab === 'ads' ? 'active' : ''} onClick={() => setOzonTab('ads')}>Кампании</button>}
                                    {visibleTabs.includes('bidder') && <button type="button" className={effectiveOzonTab === 'bidder' ? 'active' : ''} onClick={() => setOzonTab('bidder')}>Биддер<ToolHelpButton toolKey="ozon_bidder" /></button>}
                                </div>
                            )}
                            {activeDomain === 'finance' && (
                                <div className="account-tabs account-tabs--secondary">
                                    {visibleTabs.includes('unit-economics') && <button type="button" className={effectiveOzonTab === 'unit-economics' ? 'active' : ''} onClick={() => setOzonTab('unit-economics')}>Unit-экономика<ToolHelpButton toolKey="ozon_unit_economics" /></button>}
                                    {visibleTabs.includes('tax-reports') && <button type="button" className={effectiveOzonTab === 'tax-reports' ? 'active' : ''} onClick={() => setOzonTab('tax-reports')}>Налоговые отчёты<ToolHelpButton toolKey="ozon_tax_reports" /></button>}
                                </div>
                            )}

                            {effectiveOzonTab === 'assortment' && (
                                <OzonAssortmentPanel
                                    ozonAccountId={ozonAccountId}
                                    ozonBusy={ozonBusy}
                                    syncOzonFast={syncOzonFast}
                                    syncOzonData={syncOzonData}
                                    assortmentPageSafe={assortmentPageSafe}
                                    assortmentTotalPages={assortmentTotalPages}
                                    assortmentPageSize={assortmentPageSize}
                                    setAssortmentPage={setAssortmentPage}
                                    setAssortmentPageSize={setAssortmentPageSize}
                                    assortmentSearch={assortmentSearch}
                                    setAssortmentSearch={setAssortmentSearch}
                                    copyTable={copyTable}
                                    downloadCsv={downloadCsv}
                                    ozonAssortment={ozonAssortment}
                                    assortmentSliceStart={assortmentSliceStart}
                                    assortmentHeadersDisplay={assortmentHeadersDisplay}
                                    getStickyClass={getStickyClass}
                                    assortmentSort={assortmentSort}
                                    setAssortmentSort={setAssortmentSort}
                                    assortmentRowsView={assortmentRowsView}
                                    openLightbox={openLightbox}
                                    refreshOzonList={refreshOzonList}
                                />
                            )}

                            {effectiveOzonTab === 'attributes' && (
                                <OzonAttributesPanel
                                    attributesPageSafe={attributesPageSafe}
                                    attributesTotalPages={attributesTotalPages}
                                    attributesPageSize={attributesPageSize}
                                    setAttributesPage={setAttributesPage}
                                    setAttributesPageSize={setAttributesPageSize}
                                    attributesSearch={attributesSearch}
                                    setAttributesSearch={setAttributesSearch}
                                    copyTable={copyTable}
                                    downloadCsv={downloadCsv}
                                    ozonAttributes={ozonAttributes}
                                    attributesHeadersFull={attributesHeadersFull}
                                    attributesRowsView={attributesRowsView}
                                    attributesSliceStart={attributesSliceStart}
                                    attributesSliceEnd={attributesSliceEnd}
                                    attributesSort={attributesSort}
                                    setAttributesSort={setAttributesSort}
                                    ATTRIBUTES_MAX_COLUMNS={ATTRIBUTES_MAX_COLUMNS}
                                    getStickyClass={getStickyClass}
                                    openLightbox={openLightbox}
                                    refreshOzonList={refreshOzonList}
                                />
                            )}

                            {effectiveOzonTab === 'descriptions' && (
                                <OzonDescriptionPanel
                                    accountId={ozonAccountId}
                                    accountName={activeAccountName}
                                />
                            )}

                            {effectiveOzonTab === 'precision-edits' && (
                                <OzonPrecisionEdits accountId={ozonAccountId} onOpenDescriptions={() => setOzonTab('descriptions')} />
                            )}

                            {effectiveOzonTab === 'seo' && isVisible('ozon_seo') && (
                                <OzonSeoUniqualization accountId={ozonAccountId} />
                            )}

                            {effectiveOzonTab === 'seo-tags' && isVisible('ozon_seo') && (
                                <AutoSeoPanel accountId={ozonAccountId} />
                            )}

                            {effectiveOzonTab === 'seo-ai' && isVisible('ozon_seo_ai') && (
                                <OzonSeoAiPanel accountId={ozonAccountId} accountName={activeAccountName} />
                            )}

                            {effectiveOzonTab === 'statistics' && (
                                <OzonStatistics
                                    accountId={ozonAccountId}
                                    accountName={activeAccountName}
                                    openLightbox={openLightbox}
                                />
                            )}

                            {effectiveOzonTab === 'stats-logs' && (
                                <DailyStatsLogs
                                    accountId={ozonAccountId}
                                    accountName={activeAccountName}
                                    marketplace="ozon"
                                />
                            )}

                            {effectiveOzonTab === 'analytics' && isVisible('ozon_analytics') && (
                                <OzonAnalytics
                                    accountId={ozonAccountId}
                                    accountName={activeAccountName}
                                    openLightbox={openLightbox}
                                />
                            )}

                            {effectiveOzonTab === 'prices' && (
                                <OzonPricesPanel accountId={ozonAccountId} workspaceId={ozonProjectId} focusMode="prices" />
                            )}

                            {effectiveOzonTab === 'repricer' && (
                                <OzonPricesPanel accountId={ozonAccountId} workspaceId={ozonProjectId} focusMode="repricer" />
                            )}


                            {effectiveOzonTab === 'fbs' && (
                                <OzonFbsPanel
                                    accountId={ozonAccountId}
                                />
                            )}

                            {effectiveOzonTab === 'fbo' && (
                                <OzonFboPanel accountId={ozonAccountId} />
                            )}

                            {effectiveOzonTab === 'feedback' && (
                                <FeedbackPanel accountId={ozonAccountId} />
                            )}

                            {effectiveOzonTab === 'chz_print' && (
                                <OzonFbsChzPrint accountId={ozonAccountId} />
                            )}

                            {effectiveOzonTab === 'ads' && isVisible('ozon_ads') && (
                                <OzonAdsCampaigns
                                    accountId={ozonAccountId}
                                    accountName={activeAccountName}
                                    openLightbox={openLightbox}
                                />
                            )}

                            {effectiveOzonTab === 'bidder' && isVisible('ozon_ads') && (
                                <OzonBidder accountId={ozonAccountId} />
                            )}

                            {effectiveOzonTab === 'repeat-orders' && (
                                <OzonRepeatOrders accountId={ozonAccountId} initialCustomerId={customerIdFilter} />
                            )}

                            {effectiveOzonTab === 'storage' && isVisible('ozon_storage') && (
                                <OzonPaidStorage accountId={ozonAccountId} accountName={activeAccountName} />
                            )}

                            {effectiveOzonTab === 'unit-economics' && (
                                <OzonUnitEconomics ozonAccountId={ozonAccountId} />
                            )}

                            {effectiveOzonTab === 'tax-reports' && isVisible('tax_reports') && (
                                <TaxReportsPanel
                                    accountId={ozonAccountId}
                                    accountName={activeAccountName}
                                    marketplace="ozon"
                                />
                            )}
                        </>
                    )}
                </>
            )}
        </>
    );
}

function OzonSectionWrapper(props) {
    // Тариф: выбран кабинет → его план; только проект → лучший из кабинетов проекта.
    const projectCabinetIds = (props.ozonProjectCabinets || []).map(cabinet => cabinet.id);
    return (
        <PlanFeaturesProvider accountId={props.ozonAccountId || null} accountIds={projectCabinetIds}>
            <OzonSection {...props} />
        </PlanFeaturesProvider>
    );
}

export default OzonSectionWrapper;

